import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Class,
  COURSE_MATERIAL_STATUS,
  CourseMaterial,
  Student,
  Teacher,
} from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
  files?: Express.Multer.File[];
}

class CourseMaterialController {
  /**
   * Get all course materials for a class
   * @route GET /api/v1/teacher/classes/:classId/materials
   * @access Private (Teacher)
   */
  async getCourseMaterials(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      const { status, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Get teacher profile
      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to class
      const classItem = await Class.findById(classId).lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const teacherObjectId = teacher._id;
      const isTeacherAssigned = (classItem.teacherIds as any[])?.some(
        (t: mongoose.Types.ObjectId) =>
          t.toString() === teacherObjectId.toString(),
      );

      if (!isTeacherAssigned) {
        res.status(403).json({
          success: false,
          message: "You are not assigned to this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      // Build query
      const query: any = {
        classId: mongoose.Types.ObjectId.createFromHexString(classId),
        teacherId: teacherObjectId,
      };

      if (status) {
        query.status = status;
      }

      // Pagination
      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      // Fetch materials
      const [materials, totalCount] = await Promise.all([
        CourseMaterial.find({ ...query })
          .sort({ order: 1, createdAt: -1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        CourseMaterial.countDocuments({ ...query }),
      ]);

      // Get total students for progress calculation
      const totalStudents = classItem.studentIds?.length || 0;

      // Format response
      const formattedMaterials = materials.map((material: any) => {
        const accessedCount = material.accessedBy?.length || 0;
        const progress =
          totalStudents > 0
            ? Math.round((accessedCount / totalStudents) * 100)
            : 0;

        return {
          id: material._id,
          title: material.title,
          description: material.description,
          deadline: material.deadline,
          status: material.status,
          order: material.order,
          documents: material.documents || [],
          progress: {
            accessed: accessedCount,
            total: totalStudents,
            percentage: progress,
          },
          createdAt: material.createdAt,
          updatedAt: material.updatedAt,
        };
      });

      res.status(200).json({
        success: true,
        message: `${totalCount} course materials found`,
        data: {
          materials: formattedMaterials,
          pagination: {
            currentPage: pageNum,
            totalPages: Math.ceil(totalCount / limitNum),
            totalCount,
            limit: limitNum,
            hasNextPage: pageNum < Math.ceil(totalCount / limitNum),
            hasPrevPage: pageNum > 1,
          },
          class: {
            id: classItem._id,
            className: classItem.className,
            classCode: classItem.classCode,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching course materials:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch course materials",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get course material by ID
   * @route GET /api/v1/teacher/classes/:classId/materials/:id
   * @access Private (Teacher)
   */
  async getCourseMaterialById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, id } = req.params;

      if (!userId || !mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid request parameters",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Get teacher and verify access
      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      const material = await CourseMaterial.findOne({
        _id: id,
        classId,
        teacherId: teacher._id,
      }).lean();

      if (!material) {
        res.status(404).json({
          success: false,
          message: "Course material not found",
          error: "Material not found",
        } as APIResponse);
        return;
      }

      // Get class for total students
      const classItem = await Class.findById(classId).lean();
      const totalStudents = classItem?.studentIds?.length || 0;
      const accessedCount = material.accessedBy?.length || 0;
      const progress =
        totalStudents > 0
          ? Math.round((accessedCount / totalStudents) * 100)
          : 0;

      res.status(200).json({
        success: true,
        message: "Course material retrieved successfully",
        data: {
          material: {
            id: material._id,
            title: material.title,
            description: material.description,
            deadline: material.deadline,
            status: material.status,
            order: material.order,
            documents: material.documents || [],
            progress: {
              accessed: accessedCount,
              total: totalStudents,
              percentage: progress,
            },
            createdAt: material.createdAt,
            updatedAt: material.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching course material:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch course material",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * List course materials for a class (enrolled student only; excludes archived)
   * @route GET /api/v1/student/classes/:classId/materials
   */
  async getStudentClassMaterials(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      const { page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      }).lean();
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findById(classId).lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const studentObjectId = student._id;
      const isEnrolled = classItem.studentIds?.some(
        (sid: mongoose.Types.ObjectId) =>
          sid.toString() === studentObjectId.toString(),
      );
      if (!isEnrolled) {
        res.status(403).json({
          success: false,
          message: "You are not enrolled in this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      const materialFilter = {
        classId: mongoose.Types.ObjectId.createFromHexString(classId),
        status: { $ne: COURSE_MATERIAL_STATUS.ARCHIVED },
      };

      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      const [materials, totalCount] = await Promise.all([
        CourseMaterial.find({ ...materialFilter })
          .sort({ order: 1, createdAt: -1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        CourseMaterial.countDocuments(materialFilter),
      ]);

      const totalStudents = classItem.studentIds?.length || 0;

      const formattedMaterials = materials.map((material: any) => {
        const accessedCount = material.accessedBy?.length || 0;
        const progress =
          totalStudents > 0
            ? Math.round((accessedCount / totalStudents) * 100)
            : 0;
        const hasAccessed = (material.accessedBy || []).some(
          (sid: mongoose.Types.ObjectId) =>
            sid.toString() === studentObjectId.toString(),
        );

        return {
          id: material._id,
          title: material.title,
          description: material.description,
          deadline: material.deadline,
          status: material.status,
          order: material.order,
          documents: material.documents || [],
          hasAccessed,
          progress: {
            accessed: accessedCount,
            total: totalStudents,
            percentage: progress,
          },
          createdAt: material.createdAt,
          updatedAt: material.updatedAt,
        };
      });

      res.status(200).json({
        success: true,
        message: `${totalCount} course material${totalCount === 1 ? "" : "s"} found`,
        data: {
          materials: formattedMaterials,
          pagination: {
            currentPage: pageNum,
            totalPages: Math.ceil(totalCount / limitNum),
            totalCount,
            limit: limitNum,
            hasNextPage: pageNum < Math.ceil(totalCount / limitNum),
            hasPrevPage: pageNum > 1,
          },
          class: {
            id: classItem._id,
            className: classItem.className,
            classCode: classItem.classCode,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching student course materials:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch course materials",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get one course material by ID (enrolled student; not archived); records access
   * @route GET /api/v1/student/classes/:classId/materials/:id
   */
  async getStudentCourseMaterialById(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, id } = req.params;

      if (!userId || !mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid request parameters",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      }).lean();
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findById(classId).lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const studentObjectId = student._id;
      const isEnrolled = classItem.studentIds?.some(
        (sid: mongoose.Types.ObjectId) =>
          sid.toString() === studentObjectId.toString(),
      );
      if (!isEnrolled) {
        res.status(403).json({
          success: false,
          message: "You are not enrolled in this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      const material = await CourseMaterial.findOne({
        _id: id,
        classId,
        status: { $ne: COURSE_MATERIAL_STATUS.ARCHIVED },
      });

      if (!material) {
        res.status(404).json({
          success: false,
          message: "Course material not found",
          error: "Material not found",
        } as APIResponse);
        return;
      }

      await material.markAsAccessed(studentObjectId);

      const totalStudents = classItem.studentIds?.length || 0;
      const accessedCount = material.accessedBy?.length || 0;
      const progress =
        totalStudents > 0
          ? Math.round((accessedCount / totalStudents) * 100)
          : 0;
      const hasAccessed = (material.accessedBy || []).some((sid: any) =>
        sid.equals(studentObjectId),
      );

      res.status(200).json({
        success: true,
        message: "Course material retrieved successfully",
        data: {
          material: {
            id: material.id,
            title: material.title,
            description: material.description,
            deadline: material.deadline,
            status: material.status,
            order: material.order,
            documents: material.documents || [],
            hasAccessed,
            progress: {
              accessed: accessedCount,
              total: totalStudents,
              percentage: progress,
            },
            createdAt: material.createdAt,
            updatedAt: material.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching student course material:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch course material",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create a new course material
   * @route POST /api/v1/teacher/classes/:classId/materials
   * @access Private (Teacher)
   */
  async createCourseMaterial(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      const { title, description, deadline, order } = req.body;
      const files = req.files;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!title || typeof title !== "string" || title.trim().length === 0) {
        res.status(400).json({
          success: false,
          message: "Title is required",
          error: "Missing required field",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Get teacher profile
      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Verify teacher is assigned to class
      const classItem = await Class.findById(classId);
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const teacherObjectId = teacher._id;
      const isTeacherAssigned = (classItem.teacherIds as any[])?.some(
        (t: mongoose.Types.ObjectId) =>
          t.toString() === teacherObjectId.toString(),
      );

      if (!isTeacherAssigned) {
        res.status(403).json({
          success: false,
          message: "You are not assigned to this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      // Get total students for progress tracking
      const totalStudents = classItem.studentIds?.length || 0;

      // Get max order for new material
      const maxOrderMaterial = await CourseMaterial.findOne({
        classId: mongoose.Types.ObjectId.createFromHexString(classId),
      })
        .sort({ order: -1 })
        .select("order")
        .lean();

      const materialOrder = order
        ? Number.parseInt(order as string, 10)
        : (maxOrderMaterial?.order ?? 0) + 1;

      // Process uploaded files
      const documents =
        files?.map(file => {
          // Extract subdirectory from destination (e.g., "uploads/materials/" -> "materials/")
          let subDir = file.destination || "";
          subDir = subDir.replace(/^uploads[\/\\]/, "");
          if (subDir && !subDir.endsWith("/") && !subDir.endsWith("\\")) {
            subDir += "/";
          }
          subDir = subDir.replaceAll("\\", "/");

          const baseUrl =
            process.env.IMAGE_URL || "http://localhost:3080/uploads";
          const fileUrl = `${baseUrl}/${subDir}${file.filename}`;

          return {
            fileName: file.filename,
            originalName: file.originalname,
            fileSize: file.size,
            mimeType: file.mimetype,
            fileUrl,
            uploadedAt: new Date(),
          };
        }) || [];

      // Create course material
      const courseMaterial = new CourseMaterial({
        classId: mongoose.Types.ObjectId.createFromHexString(classId),
        schoolId: classItem.schoolId,
        teacherId: teacherObjectId,
        title: title.trim(),
        description: description?.trim(),
        deadline: deadline ? new Date(deadline) : undefined,
        status: COURSE_MATERIAL_STATUS.PUBLISHED,
        order: materialOrder,
        documents,
        totalStudents,
      });

      await courseMaterial.save();

      logger.info(`Course material created: ${courseMaterial.id}`, {
        materialId: courseMaterial.id,
        classId,
        teacherId: teacherObjectId.toString(),
      });

      res.status(201).json({
        success: true,
        message: "Course material created successfully",
        data: {
          material: {
            id: courseMaterial.id,
            title: courseMaterial.title,
            description: courseMaterial.description,
            deadline: courseMaterial.deadline,
            status: courseMaterial.status,
            order: courseMaterial.order,
            documents: courseMaterial.documents,
            progress: {
              accessed: 0,
              total: totalStudents,
              percentage: 0,
            },
            createdAt: courseMaterial.createdAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error creating course material:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create course material",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update course material
   * @route PUT /api/v1/teacher/classes/:classId/materials/:id
   * @access Private (Teacher)
   */
  async updateCourseMaterial(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, id } = req.params;
      const { title, description, deadline, order, status, removeDocuments } =
        req.body;
      const files = req.files;

      if (!userId || !mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid request parameters",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Get teacher and verify access
      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      const material = await CourseMaterial.findOne({
        _id: id,
        classId,
        teacherId: teacher._id,
      });

      if (!material) {
        res.status(404).json({
          success: false,
          message: "Course material not found",
          error: "Material not found",
        } as APIResponse);
        return;
      }

      // Update fields
      if (title !== undefined) material.title = title.trim();
      if (description !== undefined) material.description = description?.trim();
      if (deadline !== undefined)
        material.deadline = deadline ? new Date(deadline) : undefined;
      if (order !== undefined)
        material.order = Number.parseInt(order as string, 10);
      if (status && Object.values(COURSE_MATERIAL_STATUS).includes(status)) {
        material.status = status;
      }

      // Remove requested existing documents by media ID/fileName
      if (removeDocuments !== undefined) {
        let documentsToRemove: string[] = [];
        try {
          if (Array.isArray(removeDocuments)) {
            documentsToRemove = removeDocuments.map(String);
          } else if (typeof removeDocuments === "string") {
            const parsed = JSON.parse(removeDocuments);
            if (Array.isArray(parsed)) {
              documentsToRemove = parsed.map(String);
            } else if (removeDocuments.trim().length > 0) {
              // fallback: comma-separated list
              documentsToRemove = removeDocuments
                .split(",")
                .map(s => s.trim())
                .filter(Boolean);
            }
          }
        } catch {
          if (typeof removeDocuments === "string") {
            documentsToRemove = removeDocuments
              .split(",")
              .map(s => s.trim())
              .filter(Boolean);
          }
        }

        if (documentsToRemove.length > 0) {
          const removeSet = new Set(
            documentsToRemove.map(v => v.trim()).filter(Boolean),
          );
          material.documents = (material.documents || []).filter(
            (doc: any) =>
              !removeSet.has(String(doc.fileName ?? "").trim()) &&
              !removeSet.has(String(doc._id ?? "").trim()),
          );
        }
      }

      // Process new files if uploaded
      if (files && files.length > 0) {
        const newDocuments = files.map(file => {
          // Extract subdirectory from destination
          let subDir = file.destination || "";
          subDir = subDir.replace(/^uploads[\/\\]/, "");
          if (subDir && !subDir.endsWith("/") && !subDir.endsWith("\\")) {
            subDir += "/";
          }
          subDir = subDir.replaceAll("\\", "/");

          const baseUrl =
            process.env.IMAGE_URL || "http://localhost:3080/uploads";
          const fileUrl = `${baseUrl}/${subDir}${file.filename}`;

          return {
            fileName: file.filename,
            originalName: file.originalname,
            fileSize: file.size,
            mimeType: file.mimetype,
            fileUrl,
            uploadedAt: new Date(),
          };
        });

        if (!material.documents) {
          material.documents = [];
        }
        material.documents.push(...newDocuments);
      }

      await material.save();

      logger.info(`Course material updated: ${material.id}`, {
        materialId: material.id,
        classId,
      });

      // Get class for total students
      const classItem = await Class.findById(classId).lean();
      const totalStudents = classItem?.studentIds?.length || 0;
      const accessedCount = material.accessedBy?.length || 0;
      const progress =
        totalStudents > 0
          ? Math.round((accessedCount / totalStudents) * 100)
          : 0;

      res.status(200).json({
        success: true,
        message: "Course material updated successfully",
        data: {
          material: {
            id: material.id,
            title: material.title,
            description: material.description,
            deadline: material.deadline,
            status: material.status,
            order: material.order,
            documents: material.documents,
            progress: {
              accessed: accessedCount,
              total: totalStudents,
              percentage: progress,
            },
            updatedAt: material.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error updating course material:", error);
      res.status(500).json({
        success: false,
        message: "Failed to update course material",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete course material
   * @route DELETE /api/v1/teacher/classes/:classId/materials/:id
   * @access Private (Teacher)
   */
  async deleteCourseMaterial(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, id } = req.params;

      if (!userId || !mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid request parameters",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Get teacher and verify access
      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      const material = await CourseMaterial.findOne({
        _id: id,
        classId,
        teacherId: teacher._id,
      });

      if (!material) {
        res.status(404).json({
          success: false,
          message: "Course material not found",
          error: "Material not found",
        } as APIResponse);
        return;
      }

      await CourseMaterial.deleteOne({ _id: id });

      logger.info(`Course material deleted: ${id}`, {
        materialId: id,
        classId,
      });

      res.status(200).json({
        success: true,
        message: "Course material deleted successfully",
      } as APIResponse);
    } catch (error) {
      logger.error("Error deleting course material:", error);
      res.status(500).json({
        success: false,
        message: "Failed to delete course material",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

// Export singleton instance
export const courseMaterialController = new CourseMaterialController();
